import { useState } from "react";
import { usePatchFreeTierLimit, type FreeTierKey } from "@/api/admin/free-tier";
import { useConfirmedChange } from "../confirmChange";
import { panelTarget } from "@/pages/admin/panelTarget";
import { maxRisk, type Risk } from "../risk";
import { useAdminT } from "../useAdminT";
import { BTN, BTN_DISABLED, FIELD, LABEL, ROW } from "./styles";

interface Props {
  settingKey: FreeTierKey;
  name: string;
  risk: Risk;
  value: number;
}

/** One free-tier limit (one catalog entry = one limit). Same PATCH as the old page. */
export function FreeTierEditor({ settingKey, name, risk, value }: Props): JSX.Element {
  const t = useAdminT();
  const patch = usePatchFreeTierLimit();
  const { confirmChange, modal } = useConfirmedChange();
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState<string | null>(null);
  const dirty = draft !== value;

  const save = (): void =>
    confirmChange({ name, risk: maxRisk(risk, panelTarget("free_tier").risk) }, String(value), String(draft), () =>
      patch.mutate(
        { feature_key: settingKey, value: draft },
        {
          onSuccess: () => setStatus(t("free_tier.updated", { count: 1 })),
          onError: (e) => setStatus((e as Error).message),
        }
      )
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={ROW}>
        <input
          aria-label={settingKey}
          style={{ ...FIELD, width: 80 }}
          type="number"
          min={0}
          value={draft}
          onChange={(e) => setDraft(Number(e.target.value))}
        />
        <button style={dirty ? BTN : BTN_DISABLED} disabled={!dirty} onClick={save}>
          {t("common.save")}
        </button>
      </div>
      {status && (
        <div role="status" style={LABEL}>
          {status}
        </div>
      )}
      {modal}
    </div>
  );
}
